import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, FileText, ExternalLink, HelpCircle } from 'lucide-react';
import './AdminDocuments.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Função para converter links do Google Drive (thumbnail format é mais fiável)
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  
  const patterns = [
    /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /https:\/\/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
    /https:\/\/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
    }
  }
  
  return url;
};

const CATEGORIES = [
  { value: 'principais', label: 'Documentos Principais' },
  { value: 'planos', label: 'Planos de Atividades' },
  { value: 'regulamentos', label: 'Regulamentos Internos' },
  { value: 'relatorios', label: 'Relatórios de Atividades e Contas' }
];

const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    category: 'principais',
    order: 0,
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    if (name === 'file_url' && value) {
      processedValue = convertGoogleDriveUrl(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingDoc) {
        await axios.put(`${API}/documents/${editingDoc.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/documents`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowModal(false);
      setEditingDoc(null);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Erro ao guardar documento:', error);
      alert('Erro ao guardar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setFormData({
      title: doc.title,
      description: doc.description || '',
      file_url: doc.file_url,
      category: doc.category,
      order: doc.order,
      is_active: doc.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este documento?')) return;

    try {
      await axios.delete(`${API}/documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDocuments();
    } catch (error) {
      console.error('Erro ao eliminar documento:', error);
      alert('Erro ao eliminar documento');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      file_url: '',
      category: 'principais',
      order: 0,
      is_active: true
    });
  };

  const openNewModal = () => {
    setEditingDoc(null);
    resetForm();
    setShowModal(true);
  };

  const getCategoryLabel = (value) => {
    const cat = CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const groupedDocuments = CATEGORIES.map(cat => ({
    ...cat,
    documents: documents.filter(d => d.category === cat.value)
  }));

  return (
    <div className="admin-documents">
      <div className="page-header">
        <h1>Gestão de Documentos</h1>
        <button className="btn-primary" onClick={openNewModal}>
          <Plus size={18} />
          Novo Documento
        </button>
      </div>

      {loading ? (
        <div className="loading">A carregar...</div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <p>Ainda não existem documentos</p>
            <button className="btn-primary" onClick={openNewModal}>
              Adicionar primeiro documento
            </button>
          </div>
        ) : (
          <div className="documents-list">
            {groupedDocuments.map(group => (
              <div key={group.value} className="document-group">
                <h2>{group.label}</h2>
                {group.documents.length === 0 ? (
                  <div className="empty-category">
                    <p>Sem documentos nesta categoria</p>
                  </div>
                ) : (
                <div className="documents-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Título</th>
                        <th>Descrição</th>
                        <th>Estado</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.documents.map(doc => (
                        <tr key={doc.id}>
                          <td>
                            <div className="doc-title">
                              <FileText size={18} />
                              {doc.title}
                            </div>
                          </td>
                          <td>{doc.description || '-'}</td>
                          <td>
                            <span className={`status-badge ${doc.is_active ? 'active' : 'inactive'}`}>
                              {doc.is_active ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td>
                            <div className="actions">
                              <a 
                                href={doc.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn-icon"
                                title="Ver documento"
                              >
                                <ExternalLink size={16} />
                              </a>
                              <button 
                                className="btn-icon" 
                                onClick={() => handleEdit(doc)}
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button 
                                className="btn-icon delete" 
                                onClick={() => handleDelete(doc.id)}
                                title="Eliminar"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingDoc ? 'Editar Documento' : 'Novo Documento'}</h2>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="title">Título *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Descrição</label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Breve descrição do documento"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="file_url">URL do Ficheiro (Google Drive) *</label>
                  <input
                    type="url"
                    id="file_url"
                    name="file_url"
                    value={formData.file_url}
                    onChange={handleChange}
                    placeholder="Cole o link do Google Drive"
                    required
                  />
                  <p className="help-text">
                    <HelpCircle size={14} />
                    <span>Partilhe o ficheiro no Google Drive como "Qualquer pessoa com o link" e cole o link aqui.</span>
                  </p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">Categoria *</label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="order">Ordem</label>
                    <input
                      type="number"
                      id="order"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />
                    <span>Documento ativo (visível no site)</span>
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'A guardar...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
  );
};

export default AdminDocuments;
